import { Component, inject, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BaseSidebarComponent } from '@modules/common/components/base-sidebar/base-sidebar.component';
import { Application } from '@modules/common/types/application';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';
import { DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { createAndOpenDraft, type NewWorkbookDeps, openInEditorFullWindow } from '@modules/sheets/new-workbook';
import { injectFullWindowStart } from '@modules/common/services/full-window.service';
import { SheetFoldersService } from '@modules/sheets/services/sheet-folders.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { VirtualFolder } from '@modules/sheets/types/virtual-folder';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';

@Component({
  selector: 'app-sheets-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.less'],
  standalone: false,
})
export class SheetsSideBarComponent extends BaseSidebarComponent<SheetFolder, Sheet, VirtualFolder> implements OnDestroy {
  application = Application.sheets;
  sidebarSplitViewKey = SidebarSplitViewKey.sheets;
  selfDragDataTypes = [DragDataTypes.sheet, DragDataTypes.sheetFolder];

  public baseItems: SidebarItem[] = [
    { title: SHEETS_STRINGS.Workbooks, id: 'all_sheets', icon: 'sapphire-sheets' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
    { title: 'Follow Up', id: 'followed', icon: 'sapphire-follow-up' },
    { title: 'Snoozed', id: 'snoozed', icon: 'sapphire-snooze' },
    // Workbook templates (Wook, 2026-10-06): the Design Lab's view, after Snoozed and before Archive.
    { title: SHEETS_STRINGS['Templates'], id: 'templates', icon: 'sapphire-template' },
    { title: 'Archive', id: 'archived', icon: 'sapphire-archive' },
    { title: 'Trash', id: 'deleted', icon: 'sapphire-trash' },
  ];

  /** What "Add details" needs to create a workbook and open it (new-workbook.ts). */
  private readonly newWorkbook: NewWorkbookDeps;
  /** SBUX-1145: a workbook made here opens full-window. */
  private readonly openNew = openInEditorFullWindow(inject(Router), injectFullWindowStart(Application.sheets));

  constructor(
    injector: Injector,
    private sheetsService: SheetsService,
    private sheetFoldersService: SheetFoldersService,
  ) {
    super(injector);
    this.newWorkbook = { sheetsService, router: inject(Router) };
  }

  /**
   * Nothing is dropped into Templates: a template is made only by "Save as template", as a copy, and a workbook
   * dropped there would otherwise be read as a move to a folder the row doesn't have (`getChangesForVirtualFolder`
   * has none for it).
   */
  override dndDrop(dragData: DragData, sidebarFolder: VirtualFolder, index?: number) {
    if (sidebarFolder === 'templates') return;
    super.dndDrop(dragData, sidebarFolder, index);
  }

  handleMove(dragData: DragData, sidebarFolder: VirtualFolder) {
    const ids = this.getIds(dragData.data);

    if (sidebarFolder === 'deleted') {
      if (dragData.type === DragDataTypes.sheet) {
        this.sheetsService.delete({ ids }, true);
      } else if (dragData.type === DragDataTypes.sheetFolder) {
        this.sheetFoldersService.delete({ ids }, true);
      }
      return;
    }

    // Restore trashed/archived items first — virtual-folder lists filter out archived/deleted
    // items, so applying the changes alone would leave them stuck in Trash/Archive.
    const restoreOptions = { skipArchiveRestore: sidebarFolder === 'archived' };

    if (dragData.type === DragDataTypes.sheet) {
      const { changes, message } = Sheet.getChangesForVirtualFolder(sidebarFolder);
      moveItemsIntoContainer(
        this.sheetsService,
        dragData.data,
        () => this.sheetsService.bunchUpdate({ ids }, changes, { message }),
        restoreOptions,
      );
    } else if (dragData.type === DragDataTypes.sheetFolder) {
      const { changes, message } = SheetFolder.getChangesForVirtualFolder(sidebarFolder);
      moveItemsIntoContainer(
        this.sheetFoldersService,
        dragData.data,
        () => this.sheetFoldersService.bunchUpdate({ ids }, changes, { message }),
        restoreOptions,
      );
    }
  }

  /**
   * "Add details" on the "Add New Workbook" form a drop on the sidebar opens (gap row 37): the workbook is
   * created as the form has it and opened in the editor, as New Workbook does. It opened the unsaved draft in
   * the detail form, the flow Wook rejected on 2026-10-06.
   */
  handleMore(item: Sheet) {
    void createAndOpenDraft(this.newWorkbook, item, { open: this.openNew });
  }

  getStitchFromDragData(dragData: DragData, sidebarFolder: VirtualFolder) {
    const { changes } = Sheet.getChangesForVirtualFolder(sidebarFolder);

    return new Sheet({ ...changes, title: dragData.data[0]['title'] });
  }
}
